/**
 * impact_lights — the short-lived lights that make a blaster hit land ON the
 * world instead of in front of it.
 *
 * WHY THIS EXISTS. Bloom spreads bright PIXELS; it does not light anything. A
 * white-hot impact quad with no light beside it leaves the wall it struck
 * exactly as dark as it was — the effect reads as a sticker. One finite-range
 * point light for a few dozen milliseconds is what makes the crate, the ground
 * and the shooter's own hands flash.
 *
 * WHY A POOL. Lights are the expensive part: each one is a shader permutation's
 * worth of work on every receiver in range, and a firefight can ask for dozens
 * per second. So there is a fixed pool, and candidates compete for it.
 *
 * RANKING AND HYSTERESIS. Candidates are ranked by importance — brightness
 * falling off with distance to the viewer — and an incumbent light is only
 * evicted by a candidate meaningfully better than it (`EVICT_MARGIN`).
 * Without that margin two similar impacts swap the same slot every frame and
 * the scene strobes. A light that is merely dimmer than a newcomer keeps its
 * slot until it dies on its own; it is already short-lived.
 *
 * THE LIGHT-SET IDENTITY LAW. Pool membership is FIXED for the session and
 * every light stays PERMANENTLY visible. Flipping a light's `visible` changes
 * the collected light set, which changes the lights-node cache key, which
 * invalidates every lit material's pipeline — and the next render rebuilds the
 * whole scene's TSL graphs synchronously. StreetLampsModule live-traced that at
 * 7–12 s on real hardware in July 2026; doing it per impact made firing the gun
 * hang the game (2026-09-15). Idle slots are intensity 0 and PARKED far below
 * the world, so a zero-intensity light cannot reach anything. Only uniforms
 * ever change.
 *
 * The pool is created once and never disposed per shot (the GPU shield law:
 * nothing ever drawn is destroyed).
 */
import * as THREE from 'three/webgpu';

/** A candidate must beat the incumbent by this factor to take its slot. */
const EVICT_MARGIN = 1.6;
/** Idle slots live down here: out of every receiver's range, membership intact. */
const PARK_Y = -10000;

interface Slot {
  light: THREE.PointLight;
  /** ms remaining; <= 0 is free. */
  left: number;
  total: number;
  peak: number;
  rank: number;
}

export interface ImpactLightRequest {
  x: number; y: number; z: number;
  color: number;
  /** Peak intensity, decayed to 0 over `ms`. */
  intensity: number;
  rangeM: number;
  ms: number;
}

export class ImpactLightPool {
  private readonly slots: Slot[] = [];
  private readonly group = new THREE.Group();
  readonly stats = { taken: 0, refused: 0, evicted: 0 };

  /**
   * `capacity` is the whole budget for impact lighting. 4–8 is a sane desktop
   * starting point; the cheap tiers should pass 2.
   */
  constructor(scene: THREE.Scene, capacity: number) {
    this.group.name = 'impact-lights';
    scene.add(this.group);
    for (let i = 0; i < Math.max(0, capacity); i++) {
      // decay 2 = physical inverse-square falloff; distance bounds the cost.
      const l = new THREE.PointLight(0xffffff, 0, 6, 2);
      l.castShadow = false; // an unshadowed light leaks through thin walls — see below
      // NEVER toggled (the light-set identity law) — parked and dark instead.
      l.visible = true;
      l.position.set(0, PARK_Y, 0);
      l.name = `impact-light-${i}`;
      this.group.add(l);
      this.slots.push({ light: l, left: 0, total: 0, peak: 0, rank: 0 });
    }
  }

  get capacity(): number { return this.slots.length; }
  get active(): number { return this.slots.reduce((n, s) => n + (s.left > 0 ? 1 : 0), 0); }

  /**
   * Importance of a light at `dist` metres from the viewer.
   *
   * Brightness over distance², so a bright far flash and a dim near one can
   * compete honestly. The +1 keeps a hit at the camera from dividing by zero.
   */
  private static rankOf(intensity: number, dist: number): number {
    return intensity / ((dist + 1) * (dist + 1));
  }

  /**
   * Ask for a light. Returns true if one was allocated.
   *
   * RANGE IS CLAMPED, deliberately: these lights are unshadowed, so a large
   * radius leaks through a thin wall and lights the room next door. Keeping the
   * radius small bounds how far a leak can reach, which is cheaper and more
   * predictable than trying to shadow a 50 ms flash.
   */
  request(req: ImpactLightRequest, viewer: { x: number; y: number; z: number }): boolean {
    if (this.slots.length === 0 || req.ms <= 0 || req.intensity <= 0) return false;
    const dist = Math.hypot(req.x - viewer.x, req.y - viewer.y, req.z - viewer.z);
    const rank = ImpactLightPool.rankOf(req.intensity, dist);

    // A free slot always wins.
    let target = this.slots.find((s) => s.left <= 0) ?? null;
    if (!target) {
      // Otherwise find the weakest incumbent and evict only if clearly beaten.
      let weakest = this.slots[0];
      for (const s of this.slots) if (s.rank < weakest.rank) weakest = s;
      if (rank < weakest.rank * EVICT_MARGIN) { this.stats.refused++; return false; }
      this.stats.evicted++;
      target = weakest;
    }
    target.light.position.set(req.x, req.y, req.z);
    target.light.color.setHex(req.color);
    target.light.distance = Math.min(12, Math.max(1, req.rangeM));
    target.light.intensity = req.intensity;
    target.left = req.ms;
    target.total = req.ms;
    target.peak = req.intensity;
    target.rank = rank;
    this.stats.taken++;
    return true;
  }

  /**
   * Age every light. `dtMs` is wall time — these are ambient flashes and must
   * not stretch when the simulation hitches.
   */
  update(dtMs: number): void {
    for (const s of this.slots) {
      if (s.left <= 0) continue;
      s.left -= dtMs;
      if (s.left <= 0) {
        s.left = 0; s.rank = 0;
        s.light.intensity = 0;
        s.light.position.set(0, PARK_Y, 0); // park it; never un-visible it
        continue;
      }
      // Quadratic decay: a flash that dies fast at the end reads hotter than
      // a linear fade over the same span.
      const t = s.left / s.total;
      s.light.intensity = s.peak * t * t;
    }
  }

  dispose(): void {
    // Removing the group DOES change the light set — but this only runs on
    // module teardown (a world exit), where the rebuild is already happening.
    for (const s of this.slots) { s.light.intensity = 0; s.light.position.set(0, PARK_Y, 0); }
    this.group.removeFromParent();
  }

  /** Diagnostic: every slot's visibility must be true, forever. */
  get membershipIntact(): boolean { return this.slots.every((s) => s.light.visible); }
}
